import { useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAccountSource } from '../../lib/accountSource'
import { useLanguage } from '../../contexts/LanguageProvider'

/**
 * OAuth Callback Page
 *
 * Handles OAuth callbacks from the auth-landing server for Google/Apple login.
 *
 * Flow:
 * 1. User clicks "Continue with Google"/"Continue with Apple" on SetupPage
 * 2. Opens oauth.happytask.app/<provider>?platform=web&provider=<provider>
 *    (the query selects the provider; the path is cosmetic)
 * 3. User completes authentication with the provider
 * 4. OAuth server redirects to: https://app.happytask.app/auth/callback?access_token=...&refresh_token=...
 * 5. This page extracts tokens from query params and finishes the sign-in (`completeOAuth`: session, active check, language)
 * 6. Redirects to /home after successful session establishment
 */
export function OAuthCallbackPage() {
  const navigate = useNavigate()
  const { search } = useLocation()
  const { language } = useLanguage()
  const account = useAccountSource()

  useEffect(() => {
    const processOAuthCallback = async () => {

      // Extract tokens from query parameters
      const urlParams = new URLSearchParams(search)
      const accessToken = urlParams.get('access_token')
      const refreshToken = urlParams.get('refresh_token')
      const error = urlParams.get('error')
      const errorDescription = urlParams.get('error_description')

      // Handle OAuth errors
      if (error) {
        console.error('[OAuthCallback] OAuth error:', error, errorDescription)
        // Redirect back to setup with error message
        navigate('/?error=' + encodeURIComponent(errorDescription || error), { replace: true })
        return
      }

      // Validate tokens
      if (!accessToken || !refreshToken) {
        console.error('[OAuthCallback] Missing tokens in callback URL')
        navigate('/?error=missing_tokens', { replace: true })
        return
      }


      try {
        // Session, is_active gate and language in one step. An inactive account is
        // signed out again right away — checking before navigating avoids landing on
        // /home only to be kicked back (AuthProvider checks it again).
        const outcome = await account.completeOAuth({ accessToken, refreshToken }, language)

        if (outcome.status === 'error') {
          console.error('[OAuthCallback] Failed to set session:', outcome.message)
          navigate('/?error=' + encodeURIComponent(outcome.message), { replace: true })
          return
        }
        if (outcome.status === 'inactive') {
          navigate('/', { replace: true })
          return
        }

        // Set loginMethod flag for OAuth flow
        localStorage.setItem('loginMethod', 'oauth')
        // Same as the Electron twin in `useDeepLink.ts`: without this the
        // setup page greets a returning OAuth user with the REGISTER view,
        // because signing in on the web never marked the device as known.
        localStorage.setItem('hasLoggedInBefore', 'true')

        // No `profileSetupCompleted` from the provider's name: the profile step
        // is where a nickname AND an avatar are chosen, and the provider's full
        // legal name is not the answer that step asks for. Same rule as the
        // Electron path in `useDeepLink.ts` and in `ProtectedRoute`.

        // Clean URL (remove tokens from query params)
        window.history.replaceState(null, '', '/auth/callback')

        // Redirect to home (ProtectedRoute will handle profile setup flow if needed)
        navigate('/home', { replace: true })
      } catch (err) {
        console.error('[OAuthCallback] Unexpected error:', err)
        navigate('/?error=unexpected_error', { replace: true })
      }
    }

    processOAuthCallback()
  }, [search, navigate, language, account])

  // Show loading spinner while processing
  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      height: '100vh',
      gap: '16px',
      fontSize: '16px',
      color: 'var(--muted-foreground)'
    }}>
      <div className="spinner" style={{
        width: '40px',
        height: '40px',
        border: '4px solid rgba(0, 0, 0, 0.1)',
        borderTopColor: '#000',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
      }} />
      <p>Completing sign-in…</p>
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  )
}
